import React, { useCallback, useEffect, useState, Fragment, ReactNode } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import EmptyData from 'components/Messages/EmptyData/EmptyData';
import styled from 'styled-components';
import { getUniqueKey } from 'utils';
import { OverviewTableProps, TableColumnInterface } from 'components/Table/table.types';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import CustomIcon from 'components/Icon/CustomIcon';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { IconBox } from 'components/StyledComponents/IconBox';
import FormField from 'components/Form/FormField';
import { Div } from 'components/Elements/Div/Div';
import { useSnackbarContext } from 'context/snackbar.context';
import { useAuthContext } from 'context/auth.context';
import { hasPermissions } from 'config/ability';
import { truncateText } from 'utils/formatting';

const TableCellWrapper = styled(TableCell)<{
  $maxWidth?: string;
  $width?: string;
  $paddingLeft?: string;
}>`
  position: relative;

  ${(props) => `
    ${props.$maxWidth && `max-width: ${props.$maxWidth}`};
    ${props.$width && `width: ${props.$width}`};
    ${props.$paddingLeft && `padding-left: ${props.$paddingLeft}`};
  `}
`;

const HeaderTableCell = styled(TableCellWrapper)<{
  $cursor?: string;
  $sorted?: boolean;
  $maxWidth?: string;
  $checkboxCell?: boolean;
}>`
  ${(props) => `
    ${props.$cursor && `cursor: ${props.$cursor}`};
    ${props.$sorted && 'font-weight: 600'};
    ${props.$checkboxCell && 'width: 1rem'}
  `}
`;

const SubTableRow = styled(TableRow)`
  background-color: ${(props) => props.theme.colors.subTableBackgroundColor};
`;

const SubRowBorderCell = styled(TableCell)`
  padding: 0;
  margin-left: 50%;
  height: 1px; // this is a trick to allow children to expand to full height or width of a table cell element
  vertical-align: top;
`;

const SubRowTableCellBorder = styled.div<{ $last?: boolean }>`
  height: 100%;
  width: 50%;
  margin-left: 1.6875rem;
  margin-right: 0;
  border-left: dotted 1px ${(props) => props.theme.colors.subTableRowChainBorderColor};

  ${(props) => props.$last && 'height: 50%;'}
`;

const SubRowTableMiddleBorder = styled.div<{ $last?: boolean }>`
  height: 50%;
  border-bottom: dotted 1px ${(props) => props.theme.colors.subTableRowChainBorderColor};
  ${(props) => props.$last && 'height: 100%;'}
`;

interface ObjWithId extends Object {
  id: number | string;
}

type SubEntity<K extends ObjWithId> = K & { id: number | string };

function OverviewTable<T, K extends ObjWithId>({
  data,
  columns,
  noDataMessageOverride,
  defaultSortedCol,
  subRows,
  checkbox,
}: OverviewTableProps<T, K>): JSX.Element {
  const [sortDescending, setSortDescending] = useState<boolean>(false);
  const [sortedColIndex, setSortedColIndex] = useState<number>();
  const [expandedSubRowIndex, setExpandedSubRowIndex] = useState<number | undefined>(subRows && 0);
  const { displaySnackbarMessage } = useSnackbarContext();
  const { ability, userTenantId } = useAuthContext();

  if (data?.length && subRows?.subRowKey && !Array.isArray(data[0][subRows.subRowKey])) {
    displaySnackbarMessage('subRowField type is not an array', 'warning');
  }

  const getInitialRowOrder = useCallback(() => {
    if (defaultSortedCol) {
      return [...data].sort((a, b) =>
        String(a[defaultSortedCol]).toLowerCase() > String(b[defaultSortedCol]).toLowerCase()
          ? 1
          : -1
      );
    } else {
      return data;
    }
  }, [data, defaultSortedCol]);

  const [tableRows, setTableRows] = useState(getInitialRowOrder());

  useEffect(() => {
    setTableRows(getInitialRowOrder());
  }, [data, defaultSortedCol, getInitialRowOrder]);

  if (!data?.length) {
    return noDataMessageOverride ?? <EmptyData />;
  }

  const getSortedRows = (column: TableColumnInterface<T>) => {
    let newRows: T[] = [];
    if (column.sortBy !== undefined) {
      if (sortDescending) {
        newRows = [...data].sort((a, b) =>
          String(b[column.sortBy!]).toLowerCase() > String(a[column.sortBy!]).toLowerCase() ? 1 : -1
        );
      } else {
        newRows = [...data].sort((a, b) =>
          String(a[column.sortBy!]).toLowerCase() > String(b[column.sortBy!]).toLowerCase() ? 1 : -1
        );
      }
    }
    return newRows;
  };

  const sortRows = (column: TableColumnInterface<T>, i?: number) => {
    const sortedRows = getSortedRows(column);
    setTableRows(sortedRows);
    setSortedColIndex(i);
    setSortDescending(!sortDescending);
  };

  const toggleExpandedRow = (i: number) => {
    if (expandedSubRowIndex === i) setExpandedSubRowIndex(undefined);
    else setExpandedSubRowIndex(i);
  };

  const makeCheckBox = (row: T | T[], checked = false) => {
    if (checkbox) {
      let rows: T[] = [];

      if (!Array.isArray(row)) {
        rows = [...checkbox.checkedRows];

        if (checkbox.checkedRows.includes(row)) {
          rows.splice(checkbox.checkedRows.indexOf(row), 1);
        } else {
          rows.push(row);
        }
      } else rows = row;

      return (
        <FormField
          field={{
            checked,
            type: 'checkbox',
            onCheck: () => checkbox.onCheck!(rows),
            onChange: () => {},
          }}
          onChange={() => {}}
        />
      );
    }
    return null;
  };

  const renderCellData = (value: string | ReactNode, truncate?: number | false) => {
    if (truncate !== false && typeof value === 'string') {
      return truncateText(value || 'N/A', truncate ?? 30);
    }

    return value || 'N/A';
  };

  const allRowsChecked = checkbox?.checkedRows.length === tableRows.length;

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {checkbox && (
              <HeaderTableCell size='small'>
                {makeCheckBox(allRowsChecked ? [] : tableRows, allRowsChecked)}
              </HeaderTableCell>
            )}
            {columns.map((column, i) => (
              <Fragment key={getUniqueKey(column)}>
                <HeaderTableCell
                  align={column.align}
                  $cursor={column.sortBy ? 'pointer' : undefined}
                  onClick={() => !!column.sortBy && sortRows(column, i)}
                  $sorted={i === sortedColIndex}
                >
                  {column.label}
                  {i === sortedColIndex && <CustomIcon icon={<UnfoldMoreIcon />} />}
                </HeaderTableCell>
              </Fragment>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableRows.map((row, rowIndex) => {
            const hasSubRows =
              subRows &&
              Array.isArray(row[subRows.subRowKey]) &&
              !!(row[subRows.subRowKey] as unknown as []).length;
            const rowIsChecked = !!(
              checkbox && checkbox.checkedRows.find((checkedRow) => checkedRow === row)
            );
            const filteredSubRows =
              hasSubRows && subRows
                ? (row[subRows.subRowKey] as unknown as []).filter((subRow: SubEntity<K>) => {
                    return (
                      hasPermissions(ability, 'read', subRows.caslSubject, {
                        tenantId: userTenantId,
                      }) ||
                      (subRows.caslConditionKey &&
                        subRows.caslConditionKey.length > 0 &&
                        hasPermissions(ability, 'read', subRows.caslSubject, {
                          [subRows.caslConditionKey]: subRow.id,
                        }))
                    );
                  })
                : [];

            return (
              <Fragment key={getUniqueKey(row as unknown as NonNullable<object>)}>
                <TableRow>
                  {checkbox && (
                    <HeaderTableCell size='small' $checkboxCell>
                      {makeCheckBox(row, rowIsChecked)}
                    </HeaderTableCell>
                  )}
                  {columns.map((column, columnIndex) => (
                    <Fragment key={getUniqueKey(column)}>
                      <TableCellWrapper
                        key={getUniqueKey(column)}
                        align={column.align}
                        $width={column.width}
                        sx={column.sx}
                      >
                        {hasSubRows && filteredSubRows.length > 0 && columnIndex === 0 ? (
                          <IconBox>
                            {expandedSubRowIndex === rowIndex ? (
                              <CustomIcon
                                icon={<KeyboardArrowDownIcon />}
                                onClick={() => toggleExpandedRow(rowIndex)}
                                height='1rem'
                              />
                            ) : (
                              <CustomIcon
                                icon={<KeyboardArrowRightIcon />}
                                onClick={() => toggleExpandedRow(rowIndex)}
                                height='1rem'
                              />
                            )}
                            {renderCellData(column.value(row), column.truncateText)}
                          </IconBox>
                        ) : columnIndex === 0 && subRows ? (
                          <Div margin='0 0 0 1.25rem'>
                            {renderCellData(column.value(row), column.truncateText)}
                          </Div>
                        ) : (
                          renderCellData(column.value(row), column.truncateText)
                        )}
                      </TableCellWrapper>
                    </Fragment>
                  ))}
                </TableRow>
                {hasSubRows &&
                  subRows &&
                  filteredSubRows.length > 0 &&
                  Array.isArray(row[subRows.subRowKey]) &&
                  expandedSubRowIndex === rowIndex && (
                    <>
                      <TableRow>
                        <TableCell colSpan={12} padding='none'>
                          <Table>
                            {subRows.showHeader && (
                              <TableHead>
                                <SubTableRow>
                                  {checkbox && (
                                    <SubRowBorderCell
                                      align='center'
                                      padding='none'
                                      size='small'
                                      sx={{
                                        width: '2.2rem',
                                      }}
                                    >
                                      <SubRowTableCellBorder></SubRowTableCellBorder>
                                    </SubRowBorderCell>
                                  )}
                                  {subRows?.columns.map((subRowColumn) => (
                                    <HeaderTableCell
                                      key={getUniqueKey(subRowColumn)}
                                      align={subRowColumn.align}
                                    >
                                      {subRowColumn.label}
                                    </HeaderTableCell>
                                  ))}
                                </SubTableRow>
                              </TableHead>
                            )}
                            <TableBody>
                              {filteredSubRows.map((subRow, subRowIndex) => {
                                const last =
                                  subRowIndex ===
                                  (row[subRows.subRowKey] as unknown as []).length - 1;
                                return (
                                  <SubTableRow key={getUniqueKey(subRow)}>
                                    <SubRowBorderCell align='center' padding='none' size='small'>
                                      <SubRowTableCellBorder $last={last}>
                                        <SubRowTableMiddleBorder $last={last} />
                                      </SubRowTableCellBorder>
                                    </SubRowBorderCell>
                                    {subRows.columns.map((subRowColumn) => (
                                      <HeaderTableCell
                                        key={getUniqueKey(subRowColumn)}
                                        align={subRowColumn.align}
                                        $width={subRowColumn.width}
                                        $paddingLeft={subRowColumn.paddingLeft}
                                      >
                                        {renderCellData(
                                          subRowColumn.value(subRow, row),
                                          subRowColumn.truncateText
                                        )}
                                      </HeaderTableCell>
                                    ))}
                                  </SubTableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableCell>
                      </TableRow>
                    </>
                  )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default OverviewTable;
